import { preload } from 'react-dom';

import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';

import {
  BUY_PROPOSITIONS,
  HERO_IMAGE_WIDTHS,
  SELL_PROPOSITIONS,
} from '../constants';
import { getFallbackHeroContent } from '../helpers/getFallbackHeroContent';
import {
  type DualPropositionHeroContent,
  type HomepageHeroContent,
} from '../types';

const IMAGE_SIZES = '(min-width: 1024px) 50vw, 100vw';

function toSrcSet({ mobileImageUrl, desktopImageUrl }: HomepageHeroContent) {
  return `${mobileImageUrl} ${HERO_IMAGE_WIDTHS.mobile}w, ${desktopImageUrl} ${HERO_IMAGE_WIDTHS.desktop}w`;
}

export function useDualPropositionContent(
  heroContent?: DualPropositionHeroContent
) {
  const pathname = usePathname();
  const t = useTranslations('home');
  const checked = pathname.includes('/start-to-sell/');

  const { buy, sell } = heroContent ?? getFallbackHeroContent(t);

  preload(sell.mobileImageUrl, {
    as: 'image',
    fetchPriority: checked ? 'high' : 'low',
    imageSrcSet: toSrcSet(sell),
    imageSizes: IMAGE_SIZES,
  });

  preload(buy.mobileImageUrl, {
    as: 'image',
    fetchPriority: checked ? 'low' : 'high',
    imageSrcSet: toSrcSet(buy),
    imageSizes: IMAGE_SIZES,
  });

  const propositionsConfig = checked ? SELL_PROPOSITIONS : BUY_PROPOSITIONS;

  const propositions = propositionsConfig.map(
    ({ icon, titleKey, descriptionKey, href }) => ({
      icon,
      title: t(titleKey),
      description: t(descriptionKey),
      href,
    })
  );

  const active = checked ? sell : buy;
  const { title, subtext, mobileImageUrl, desktopImageUrl, imageAltText } =
    active;

  const ctaText = checked
    ? t('DualProposition.Sell.Cta')
    : t('DualProposition.Buy.Cta');

  const href = checked
    ? `/signup/?redirect=${encodeURIComponent('/products/create/')}`
    : '/explore/shop-now/';

  return [
    checked,
    {
      images: {
        mobile: mobileImageUrl,
        desktop: desktopImageUrl,
        alt: imageAltText,
        srcSet: toSrcSet(active),
        sizes: IMAGE_SIZES,
      },
      title,
      description: subtext,
      cta: {
        text: ctaText,
        href,
      },
      propositions,
    },
  ] as const;
}
